import type { CollisionMask } from './collisionMask';
import type { PhysicsStep } from './physicsStep';
import { WA_PHYSICS } from '../core/constants';
import { AimStepper } from './aimStepper';
import type { ModeConfig } from '../core/gameModes';
import type { EntityRegistry } from '../core/entityRegistry';
import type { WeaponSelectionManager } from '../core/weaponSelectionManager';
import type { ChargeStateManager, ChargeType } from '../core/chargeStateManager';
import type { TargetingSystem } from '../core/targetingSystem';
import type { ToolSystemManager } from '../core/toolSystemManager';
import type { TurnManager } from '../core/turnManager';
import type { WorldStabilityCheckerImpl } from '../core/worldStabilityChecker';
import type { SuddenDeathManager } from '../core/suddenDeathManager';
import type { CrateManager } from '../core/crateManager';
import type { JetpackManager } from '../core/jetpackManager';
import type { GameState } from '../logic/gameState';
import type { Team } from '../logic/team';
import type { Worm } from '../entities/worm';
import type { WeatherSystem } from './weather';
import type { SuperSheep } from '../weapons/superSheep';
import { releaseRope, isRopeActive, applyRopeInput } from '../weapons/ninjaRope';
import { releaseBungee, isBungeeActive } from '../weapons/bungee';

export interface SimInputState {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  jump: boolean;
  fire: boolean;
  charging: boolean;
}

export interface SimulationContext {
  state: GameState;
  terrain: CollisionMask;
  /** Declarative mode ruleset (aim stepping, limits, ...). */
  mode: ModeConfig;
  entityRegistry: EntityRegistry;
  weaponSelection: WeaponSelectionManager;
  chargeState: ChargeStateManager;
  targetingSystem: TargetingSystem;
  toolSystem: ToolSystemManager;
  turnManager: TurnManager;
  worldStabilityChecker: WorldStabilityCheckerImpl;
  suddenDeathManager: SuddenDeathManager;
  crateManager: CrateManager;
  jetpackManager: JetpackManager;
  getWorms(): Worm[];
  /** The tracked super sheep currently in flight, if any (steered by inputs). */
  getFlyingSuperSheep(): SuperSheep | null;
  /** Round-based weather events (rolled by the authority only). */
  weather?: WeatherSystem;
}

export interface SimulationCallbacks {
  /** A new turn began (sound, sprite refresh, camera reset, ammo panel, tracked-entity resets). */
  onTurnStarted(currentTeam: Team | null): void;
  /** The movement phase ended (reset client input state). */
  onMovementPhaseEnded(): void;
  /** The turn advanced (timer expiry or drown) — broadcast turn end on network games. */
  onTurnAdvanced(): void;
  /** Double-tap jump bookkeeping lives client-side; returns true when a pending jump should fire now. */
  shouldJumpNow(): boolean;
  /**
   * Whether held up/down input drives aiming. True for offline/local play;
   * false on the server, where aim arrives as discrete 'aim' actions so the
   * client-predicted angle and the authoritative angle match exactly.
   */
  aimFromHeldInput(): boolean;
  onWormJumped(): void;
  updateGirderPreview(): void;
  isWaitingForCrates(): boolean;
}

/**
 * Headless turn-flow + input-application simulation, extracted from Game.
 * Composes PhysicsStep; presentation and networking happen via callbacks so
 * the same code can run in the browser (local play) and on the server.
 */
export class Simulation {
  private lastTurnSequence = 0;
  private lastPhase: string = 'setup';
  private totalTurnCount = 0;
  private drownDelay = 0;
  private drownedActiveWorm = false;
  private drownTurnSequence = 0;
  private readonly TARGETING_CURSOR_SPEED = 300;
  private aimStepper = new AimStepper();

  constructor(
    private ctx: SimulationContext,
    private physicsStep: PhysicsStep,
    private cb: SimulationCallbacks,
  ) {}

  /** Turn-start detection and movement-phase-exit handling. Run first each tick. */
  beginTick(isAuthority = true): void {
    const { state } = this.ctx;
    const currentPhase = state.phase;
    const turnChanged = state.turnSequence !== this.lastTurnSequence;
    if (turnChanged && currentPhase === 'playing') {
      this.lastTurnSequence = state.turnSequence;
      this.totalTurnCount++;
      this.ctx.suddenDeathManager.checkSuddenDeath(this.totalTurnCount);

      this.ctx.toolSystem.reset();
      this.ctx.worldStabilityChecker.resetForceStableAttempts();
      this.ctx.turnManager.resetForNewTurn();

      for (const team of state.teams) {
        for (const worm of team.worms) {
          worm.immuneToFallDamage = false;
        }
      }

      this.cb.onTurnStarted(state.getCurrentTeam());

      // Weather is rolled by the authority only; online clients mirror it
      // via TurnSync.weather.
      if (isAuthority) {
        this.ctx.weather?.onTurnStarted();
      }

      // Crates are spawned by the authority only; online clients receive
      // them via snapshots (a client-side spawn would leave waitingForCrates
      // stuck forever, freezing the predicted worm).
      if (isAuthority && this.ctx.crateManager.shouldSpawnCrate()) {
        this.ctx.crateManager.spawnRandomCrate();
      }
    }

    const wasMovementAllowed = this.lastPhase === 'playing' || this.lastPhase === 'retreat';
    const isMovementAllowed = currentPhase === 'playing' || currentPhase === 'retreat';
    if (wasMovementAllowed && !isMovementAllowed) {
      this.endMovementPhase();
    }
    this.lastPhase = currentPhase;
  }

  private endMovementPhase(): void {
    for (const team of this.ctx.state.teams) {
      for (const worm of team.worms) {
        if (isRopeActive(worm)) {
          releaseRope(worm);
        }
        if (isBungeeActive(worm)) {
          releaseBungee(worm);
        }
        if (worm.state !== 'dead' && worm.isGrounded) {
          worm.state = 'idle';
        }
      }
    }
    this.cb.onMovementPhaseEnded();
  }

  /** Timers + input application for the simulation authority. */
  applyAuthorityInput(deltaTime: number, input: SimInputState, currentWorm: Worm | null): void {
    const { state, terrain } = this.ctx;
    const pauseTimer =
      this.cb.isWaitingForCrates() || state.phase === 'damage_resolution' || this.drownDelay > 0;
    const turnBefore = state.currentTeamIndex;
    state.updateTimers(deltaTime, true, pauseTimer);
    const turnAfter = state.currentTeamIndex;
    if (turnBefore !== turnAfter) {
      this.ctx.turnManager.resetForNewTurn();
      this.ctx.worldStabilityChecker.resetForceStableAttempts();
      this.cb.onTurnAdvanced();
    }

    const flyingSuperSheep = this.ctx.getFlyingSuperSheep();

    if (this.ctx.targetingSystem.isActive()) {
      const isGirder = this.ctx.weaponSelection.isWeaponSelected('girder');
      const anyDpadInput = input.left || input.right || input.up || input.down;
      if (anyDpadInput) {
        this.ctx.targetingSystem.setWithDpad(true);
        const speed = this.TARGETING_CURSOR_SPEED * deltaTime;
        let dx = 0;
        let dy = 0;
        if (input.left) dx -= speed;
        if (input.right) dx += speed;
        if (!isGirder) {
          if (input.up) dy -= speed;
          if (input.down) dy += speed;
        }
        this.ctx.targetingSystem.moveCursor(dx, dy);
      }

      if (isGirder) {
        this.cb.updateGirderPreview();
      }
    } else if (currentWorm && currentWorm.jetpackActive) {
      this.ctx.jetpackManager.handleJetpackInput(deltaTime, currentWorm);
    } else if (flyingSuperSheep) {
      flyingSuperSheep.steeringLeft = input.left;
      flyingSuperSheep.steeringRight = input.right;
    } else if (
      currentWorm &&
      state.canMove() &&
      !this.cb.isWaitingForCrates() &&
      !currentWorm.hasPendingDamage()
    ) {
      const otherWorms = this.ctx.getWorms().filter((w) => w.id !== currentWorm.id);
      const moveStep = WA_PHYSICS.WORM.WALK_SPEED / WA_PHYSICS.TICK_RATE;
      if (input.left || input.right) {
        const dir = input.left ? 'left' : 'right';
        if (state.tryConsumeMovement(moveStep)) {
          currentWorm.move(dir, terrain, otherWorms);
        } else {
          // Movement budget exhausted: the worm may still turn in place.
          currentWorm.facing = dir;
          currentWorm.stopWalking();
        }
      } else {
        currentWorm.stopWalking();
      }

      if (this.cb.aimFromHeldInput()) {
        const steps = this.aimStepper.update(deltaTime, input.up, input.down);
        if (steps !== 0) {
          currentWorm.adjustAim(steps * this.ctx.mode.aim.stepDegrees);
        }
      }

      if (currentWorm.isGrounded && this.cb.shouldJumpNow()) {
        currentWorm.jump(terrain);
        this.cb.onWormJumped();
      }
    } else if (currentWorm && (currentWorm.state === 'walking' || currentWorm.hasPendingDamage())) {
      currentWorm.state = 'idle';
    }

    const chargeType = this.getChargeType();
    let wormHandPosition: { x: number; y: number } | undefined;
    if (currentWorm && chargeType !== 'none' && input.charging) {
      const bounds = currentWorm.getBounds();
      wormHandPosition = {
        x: currentWorm.position.x + bounds.width / 2 + (currentWorm.facing === 'right' ? 8 : -8),
        y: currentWorm.position.y + bounds.height / 2 - 5,
      };
    }
    this.ctx.chargeState.update(deltaTime, input.charging, chargeType, wormHandPosition);

    if (currentWorm && isRopeActive(currentWorm)) {
      applyRopeInput(currentWorm, input.left, input.right, input.up, input.down);
    }

    if (currentWorm && this.ctx.toolSystem.isActive()) {
      this.ctx.toolSystem.update(deltaTime, currentWorm);
    }
  }

  updateTimersNonAuthority(deltaTime: number): void {
    this.ctx.state.updateTimers(deltaTime, false);
  }

  stepPhysics(deltaTime: number, isAuthority: boolean): void {
    this.physicsStep.update(deltaTime, isAuthority);
  }

  updateDamageResolution(deltaTime: number): void {
    this.ctx.turnManager.updateDamageResolution(deltaTime, () => this.getWormsWithPendingDamage());
  }

  private getWormsWithPendingDamage(): Worm[] {
    const worms: Worm[] = [];
    for (const team of this.ctx.state.teams) {
      for (const worm of team.worms) {
        if (worm.state !== 'dead' && worm.hasPendingDamage()) {
          worms.push(worm);
        }
      }
    }
    return worms;
  }

  /** Record a drowned worm; turn advance happens after a short delay (see updateDrownDelay). */
  notifyWormDrowned(isActiveWorm: boolean): void {
    this.drownDelay = 1.5;
    this.drownedActiveWorm = isActiveWorm;
    this.drownTurnSequence = this.ctx.state.turnSequence;
  }

  updateDrownDelay(deltaTime: number, isAuthority: boolean): void {
    if (this.drownDelay > 0) {
      this.drownDelay -= deltaTime;
      if (this.drownDelay <= 0) {
        this.drownDelay = 0;
        const wasActiveWorm = this.drownedActiveWorm;
        const sameTurn = this.drownTurnSequence === this.ctx.state.turnSequence;
        this.drownedActiveWorm = false;
        if (isAuthority && sameTurn) {
          this.ctx.state.checkGameOver();
          if (this.ctx.state.phase !== 'finished' && wasActiveWorm) {
            this.ctx.state.advanceTurn();
            this.cb.onTurnAdvanced();
          }
        }
      }
    }
  }

  private getChargeType(): ChargeType {
    const category = this.ctx.weaponSelection.getCategory();
    const isPowerCharging = this.ctx.weaponSelection.isPowerCharging();

    if (category === 'thrown' && isPowerCharging) return 'thrown';
    if (category === 'projectile' && isPowerCharging) return 'rocket';
    if (category === 'homing' && this.ctx.targetingSystem.hasLockedTarget()) return 'homing';
    return 'none';
  }

  getLastPhase(): string {
    return this.lastPhase;
  }

  getTotalTurnCount(): number {
    return this.totalTurnCount;
  }

  setTotalTurnCount(count: number): void {
    this.totalTurnCount = count;
  }
}
